import React, { useState, useRef, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useGlobalSearch } from "./useGlobalSearch";
import { useRecentSearches } from "./useRecentSearches";
import GlobalSearchResultBox from "./GlobalSearchResultBox";
import RecentSearchesBox from "./RecentSearchesBox";
import { navigateSearchResult } from "./globalSearchTransformer";
import { recordCommittedSearch } from "./recordSearch";
import useUrlDialog from "../../../Hooks/useUrlDialog";
import UrlDialog from "../../../components/dialog/UrlDialog";
import { dispatchNotification } from "../../../components/Utils";
import { useGuardedNavigate } from "../../../contexts/NavigationGuardContext";

// Bar expand animation duration. Single source for both the CSS transition and
// the dropdown gate timer below, so they can never drift out of sync.
const EXPAND_MS = 250;

export const GlobalSearchInput = ({ onOpenChange }) => {
  const { t, i18n } = useTranslation();
  // Guarded, not react-router's raw useNavigate — so a search-result pick
  // is nullified like every other CTA while the non-DBT registration
  // screen's leave-guard is active (RewampNonDbtRegistrationForm).
  const navigate = useGuardedNavigate();
  const dispatch = useDispatch();
  // Outbound (YouTube video) hits go through the app's shared external-link
  // interstitial, same as the Footer / News section — never a raw window.open.
  const {
    open: urlDialogOpen,
    externalUrl,
    handleLinkClick,
    handleClose: handleUrlDialogClose,
  } = useUrlDialog();
  const { query, setQuery, results, loading } = useGlobalSearch();
  const {
    recentSearches,
    loading: loadingRecent,
    isLoggedIn,
    fetchRecent,
    deleteOne,
    clearAll,
  } = useRecentSearches();

  const [open, setOpen] = useState(false);
  // `open` drives the results dropdown (needs a query); `focused` drives the
  // bar expansion so clicking inside opens it immediately, with no typing.
  const [focused, setFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);

  const expanded = open || focused;

  // The bar animates open over EXPAND_MS. Gate the results dropdown so it only
  // mounts once that expansion has finished — otherwise it flashes at the
  // collapsed width (GlobalSearchResultBox reads anchorEl.offsetWidth on mount,
  // and focus can flip `expanded` + `open` in the same frame when query text
  // is already present).
  const [expandedSettled, setExpandedSettled] = useState(false);

  useEffect(() => {
    if (!expanded) {
      setExpandedSettled(false);
      return undefined;
    }
    const id = setTimeout(() => setExpandedSettled(true), EXPAND_MS);
    return () => clearTimeout(id);
  }, [expanded]);

  const showDropdown = open && expandedSettled;

  // Recent-search history panel: shown when a logged-in farmer focuses the
  // (still-empty) bar, before they've typed a searchable query. Mutually
  // exclusive with the results dropdown, which needs query.length >= 3.
  const queryShort = query.trim().length < 3;
  const showRecent =
    focused &&
    !open &&
    queryShort &&
    isLoggedIn &&
    expandedSettled &&
    (loadingRecent || recentSearches.length > 0);

  const flatResults = useMemo(() => {
    return results.reduce((acc, group) => [...acc, ...group.items], []);
  }, [results]);

  useEffect(() => {
    if (query.length >= 3 && !open) {
      setOpen(true);
    } else if (query.length < 3) {
      setOpen(false);
    }
  }, [query]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [flatResults]);

  // Pull the farmer's recent searches whenever they focus the empty bar (and
  // again each time the query drops back below the search threshold, so a
  // just-recorded search appears). fetchRecent de-dupes concurrent calls.
  useEffect(() => {
    if (focused && isLoggedIn && queryShort) fetchRecent();
  }, [focused, isLoggedIn, queryShort, fetchRecent]);

  // Let the parent (navbar) hand free space to the bar whenever it's expanded —
  // on focus (click inside) as well as when the results dropdown is open.
  useEffect(() => {
    onOpenChange?.(expanded);
  }, [expanded, onOpenChange]);

  // Fire the "link unavailable" toast (same Snackbar mechanism as the scheme
  // notices) when a chosen result has no tangible target.
  const notifyResultUnavailable = () => {
    dispatchNotification("error", ["REWAMP_SEARCH_LINK_UNAVAILABLE"], dispatch);
  };

  // Persist a committed query to the farmer's recent-search history. Called only
  // at commit points (picking a result, Enter, re-running a recent) — never from
  // the browse-only live search — so history holds whole terms, not keystroke
  // prefixes. No-ops for guests / sub-threshold queries; best-effort.
  const recordQuery = (text) => {
    recordCommittedSearch(text, i18n.language?.startsWith("hi") ? "hi" : "en");
  };

  // Single entry point for acting on a chosen result — shared by click
  // (GlobalSearchResultBox) and the keyboard Enter handler below. Videos open
  // via the external-link interstitial; missing targets raise the toast.
  const activateResult = (item) => {
    // Record the query the farmer actually searched (not the result title) — the
    // act of picking a result is a firm commit signal.
    recordQuery(query);
    navigateSearchResult(item, {
      navigate,
      openExternal: handleLinkClick,
      onUnavailable: notifyResultUnavailable,
    });
  };

  const handleKeyDown = (e) => {
    // Escape closes whichever panel is showing — results OR the recent-search
    // history (which runs with open=false), matching the footer's "Esc to close".
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setFocused(false);
      inputRef.current?.blur();
      return;
    }

    if (!open && query.length >= 3) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        return;
      }
    }

    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < flatResults.length) {
        setOpen(false);
        // activateResult() records the query itself.
        activateResult(flatResults[selectedIndex]);
      } else {
        // Enter with no highlighted row still commits the typed query to history.
        recordQuery(query);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFocused(false);
  };

  // Clear the query (which closes the dropdown via the query.length < 3 effect)
  // but keep the bar focused so the user can immediately type a new search.
  const handleClearAll = () => {
    setQuery("");
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Re-run a past search: fill the bar (which fires the debounced live search
  // and swaps the recent panel for the results dropdown) and keep focus. Also
  // re-record it so re-running an old term floats it back to the top of history.
  const handleRecentSelect = (text) => {
    setQuery(text);
    recordQuery(text);
    inputRef.current?.focus();
  };


  return (
    <>
    <ClickAwayListener onClickAway={handleClose}>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          // On expand the wrapper claims the free navbar space the parent
          // yields; collapses back to its content width when closed.
          flexGrow: expanded ? 1 : 0,
          minWidth: 0,
          width: expanded ? "100%" : "auto",
          // Keep the (maxWidth-capped) input pinned to the right so it stays a
          // fixed gap from the profile section and extends leftward as it grows.
          justifyContent: "flex-end",
        }}
      >
        <Box
          ref={anchorRef}
          onClick={() => inputRef.current?.focus()}
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "rgba(255,255,255,0.08)",
            border: "1px solid",
            borderColor: expanded ? "accent.butterscotch" : "rgba(255,255,255,0.4)",
            borderRadius: "8px",
            height: "38px",
            // Resting: pre-existing collapsed sizes. Expanded: fill the wrapper
            // but cap with maxWidth so the bar stays sane on tablet / Android.
            width: expanded ? "100%" : { xs: "100%", sm: "200px", md: "250px" },
            maxWidth: expanded ? { xs: "100%", sm: 480, md: 600 } : "none",
            px: 1.5,
            // Directional transition: animate width ONLY while expanding (the
            // wrapper is a concrete-width flex item then, so `100%` resolves
            // cleanly and the bar grows leftward smoothly). On collapse, width
            // is excluded so it snaps instantly — animating it against the
            // wrapper reverting to `auto` is what caused the shrink jitter.
            transition: expanded
              ? `width ${EXPAND_MS}ms ease, max-width ${EXPAND_MS}ms ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease`
              : "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
              borderColor: "rgba(255,255,255,0.6)",
            },
            "&:focus-within": {
              borderColor: "accent.butterscotch",
              boxShadow: "0 0 0 2px rgba(208, 140, 42, 0.4)", // Focus ring mapping
            },
          }}
        >
          <SearchIcon
            aria-hidden="true"
            sx={{ color: "background.tertiaryGreen", fontSize: "20px", mr: 1 }}
          />
          <InputBase
            inputRef={inputRef}
            name="global-search"
            type="text"
            autoComplete="off"
            placeholder={t("REWAMP_GLOBAL_SEARCH")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setFocused(true);
              if (query.length >= 3) setOpen(true);
            }}
            aria-label={t("REWAMP_GLOBAL_SEARCH")}
            sx={{
              color: "background.white",
              width: "100%",
              fontSize: "0.875rem",
              "& .MuiInputBase-input": {
                p: 0,
                "&::placeholder, &::-webkit-input-placeholder, &::-moz-placeholder, &:-ms-input-placeholder": {
                  color: "neutral.placeholderOnGreen",
                  WebkitTextFillColor: (theme) =>
                    theme.palette.neutral?.placeholderOnGreen || "#95D5B2",
                  opacity: 1,
                },
              },
            }}
            inputProps={{
              role: "combobox",
              "aria-expanded": showDropdown,
              "aria-controls": "global-search-results",
              "aria-activedescendant":
                selectedIndex >= 0 ? `search-result-${selectedIndex}` : undefined,
              // Third-party managers ignore autoComplete="off"; these opt them out too.
              "data-1p-ignore": true,
              "data-lpignore": "true",
            }}
          />
        </Box>

        <GlobalSearchResultBox
          open={showDropdown}
          anchorEl={anchorRef.current}
          results={results}
          flatResults={flatResults}
          loading={loading}
          onClose={handleClose}
          onClearAll={handleClearAll}
          onActivate={activateResult}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />

        <RecentSearchesBox
          open={showRecent}
          anchorEl={anchorRef.current}
          recentSearches={recentSearches}
          loading={loadingRecent}
          onSelect={handleRecentSelect}
          onDeleteOne={deleteOne}
          onClearAll={clearAll}
        />
      </Box>
    </ClickAwayListener>

      {/* Interstitial shown before leaving the site for a video (YouTube) link.
          Uses a video-specific disclaimer key instead of UrlDialog's generic
          `dialogContent` default. */}
      <UrlDialog
        open={urlDialogOpen}
        externalUrl={externalUrl}
        handleClose={handleUrlDialogClose}
        content="REWAMP_SEARCH_VIDEO_DIALOG"
        t={t}
      />
    </>
  );
};

export default GlobalSearchInput;
